import React, { useMemo, useState, lazy, Suspense, useEffect } from 'react';
import { isIpad, isDesktop } from '../../utils/ScreenSizeHelper';

// ✅ Lazy-load (code-split) these modules so they won't load until used
const BankAccount = lazy(() => import('./BankAccount'));
const Businesslicense = lazy(() => import('./Businesslicense'));
const AccountInfo = lazy(() => import('./AccountInfo'));

const AccountSettings = () => {
  const sections = useMemo(
    () => [
      { id: '1', title: 'Account info' },
      { id: '2', title: 'Business license' },
      { id: '3', title: 'Bank Account' },
    ],
    []
  );

  const [selectedId, setSelectedId] = useState('1');

  // ✅ Only allow iPad/Desktop layout
  const allowLayout = isIpad || isDesktop;

  const MAX_WIDTH = isIpad ? 800 : 1200; // iPad-friendly, desktop can breathe

  const styles = {
    page: {
      background: '#ffffff',
      color: '#111827',
      minHeight: '100vh',
      fontFamily:
        'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
    },
    headerWrap: {
      borderBottom: '1px solid #e5e7eb',
      position: 'sticky',
      top: 0,
      zIndex: 50,
      background: 'rgba(255,255,255,0.95)',
      backdropFilter: 'blur(8px)',
    },
    header: {
      maxWidth: MAX_WIDTH,
      margin: '0 auto',
      padding: '14px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
    },
    brand: { display: 'flex', flexDirection: 'column', lineHeight: 1.1 },
    brandTop: { fontWeight: 800, fontSize: 16 },

    // ✅ Always 2-column for iPad/Desktop
    shell: {
      display: 'flex',
      flexDirection: 'row',
      maxWidth: MAX_WIDTH,
      margin: '0 auto',
      padding: '22px 16px 80px',
    },

    // ✅ Sticky TOC (tablet/desktop)
    toc: {
      position: 'sticky',
      top: 92,
      alignSelf: 'start',
      border: '1px solid #e5e7eb',
      borderRadius: 5,
      padding: 14,
      background: '#fff',
      width: '350px',
      marginRight: '5px',
    },
    tocLink: (isActive) => ({
      display: 'block',
      padding: '8px 10px',
      borderRadius: 5,
      fontSize: 16,
      color: isActive ? '#111827' : '#374151',
      background: isActive ? '#f3f4f6' : 'transparent',
      cursor: 'pointer',
      textDecoration: 'none',
      lineHeight: 1.3,
      border: '1px solid transparent',
    }),

    contentCard: {
      border: '1px solid #e5e7eb',
      borderRadius: 5,
      background: '#fff',
      padding: '24px 22px',
      width: '100%',
    },

    // ✅ Mobile blocked screen
    mobileBlockWrap: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 18,
      background: '#ffffff',
    },
    mobileBlock: {
      width: '100%',
      maxWidth: 520,
      border: '1px solid #e5e7eb',
      borderRadius: 14,
      padding: 18,
      background: '#fff',
      color: '#111827',
    },
    mobileTitle: { fontWeight: 900, fontSize: 16, marginBottom: 6 },
    mobileText: { fontSize: 13, color: '#6b7280', lineHeight: 1.6 },
  };

  // ✅ Optional: prefetch other tab code once after mount
  useEffect(() => {
    import('./AccountInfo');
    import('./Businesslicense');
    import('./BankAccount');
  }, []);

  const PanelLoader = () => (
    <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>Loading…</div>
  );

  // ✅ Remove mobile view entirely (only allow iPad/Desktop)
  if (!allowLayout) {
    return (
      <div style={styles.mobileBlockWrap}>
        <div style={styles.mobileBlock}>
          <div style={styles.mobileTitle}>Tablet/Desktop only</div>
          <div style={styles.mobileText}>
            This page is designed for iPad and desktop. Please open it on a larger screen.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.headerWrap}>
        <div style={styles.header}>
          <div style={styles.brand}>
            <div style={styles.brandTop}>Account Settings</div>
          </div>
        </div>
      </div>

      {/* Layout */}
      <div style={styles.shell}>
        <aside style={styles.toc}>
          {sections.map((s) => (
            <div
              key={s.id}
              style={styles.tocLink(selectedId === s.id)}
              onClick={() => setSelectedId(s.id)}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setSelectedId(s.id);
              }}
            >
              {s.title}
            </div>
          ))}
        </aside>

        {/* ✅ Keep all panels mounted; just hide inactive ones */}
        <main style={styles.contentCard}>
          <Suspense fallback={<PanelLoader />}>
            <div style={{ display: selectedId === '1' ? 'block' : 'none' }}>
              <AccountInfo />
            </div>

            <div style={{ display: selectedId === '2' ? 'block' : 'none' }}>
              <Businesslicense />
            </div>

            <div style={{ display: selectedId === '3' ? 'block' : 'none' }}>
              <BankAccount />
            </div>
          </Suspense>
        </main>
      </div>

      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          button { display: none !important; }
          aside { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default AccountSettings;
